import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DailyDiscoveryService, { isUsableArtistImageUrl } from '../../services/dailyDiscovery';
import { searchArtistForProvider } from '../../services/musicProvider';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import PageHeader from '../ui/PageHeader';
import ArtistCard from '../ui/ArtistCard';
import ListenModal from '../ui/ListenModal';
import '../../styles/discoveriesWall.css';

/** Cover wall: se manca/fallisce, prova URL successive poi artisti via provider */
const DiscoveryWallCover = ({ coverImage, coverUrls = [], coverCandidates = [] }) => {
  const initialUrls = (coverUrls?.length ? coverUrls : (coverImage ? [coverImage] : []))
    .filter(Boolean);
  const [resolvedUrl, setResolvedUrl] = useState(initialUrls[0] || null);
  const [busy, setBusy] = useState(!initialUrls[0] && coverCandidates?.length > 0);
  const attemptRef = React.useRef({ urlIdx: 0, nameIdx: 0 });

  const resolveFromCandidates = async (startNameIdx = 0) => {
    for (let i = startNameIdx; i < (coverCandidates?.length || 0); i += 1) {
      const name = coverCandidates[i];
      try {
        const data = await searchArtistForProvider(name, null, null, null, true);
        const url = data?.images?.find((img) => isUsableArtistImageUrl(img?.url))?.url;
        if (url) {
          attemptRef.current.nameIdx = i + 1;
          setResolvedUrl(url);
          setBusy(false);
          return true;
        }
      } catch (e) {
        // continua
      }
      attemptRef.current.nameIdx = i + 1;
    }
    setBusy(false);
    setResolvedUrl(null);
    return false;
  };

  useEffect(() => {
    attemptRef.current = { urlIdx: 0, nameIdx: 0 };
    const urls = (coverUrls?.length ? coverUrls : (coverImage ? [coverImage] : [])).filter(Boolean);
    if (urls[0]) {
      setResolvedUrl(urls[0]);
      setBusy(false);
      return undefined;
    }
    // Nessuna URL: risolvi subito dai nomi artisti (1° → 2° → …)
    let cancelled = false;
    setBusy(true);
    (async () => {
      if (cancelled) return;
      await resolveFromCandidates(0);
    })();
    return () => { cancelled = true; };
  }, [coverImage, coverUrls?.join('|'), coverCandidates?.join('|')]);

  const tryNext = async () => {
    const urls = (coverUrls?.length ? coverUrls : (coverImage ? [coverImage] : [])).filter(Boolean);
    const nextIdx = attemptRef.current.urlIdx + 1;
    if (nextIdx < urls.length) {
      attemptRef.current.urlIdx = nextIdx;
      setResolvedUrl(urls[nextIdx]);
      return;
    }
    setBusy(true);
    await resolveFromCandidates(attemptRef.current.nameIdx);
  };

  if (!resolvedUrl) {
    return <div className={`discoveries-wall__card-ph${busy ? ' is-loading' : ''}`} />;
  }

  return (
    <img
      src={resolvedUrl}
      alt=""
      loading="lazy"
      onError={tryNext}
    />
  );
};

const DailyDiscovery = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  const [discovery, setDiscovery] = useState(null);
  const [representativeArtist, setRepresentativeArtist] = useState(null);
  const [loadingArtistImage, setLoadingArtistImage] = useState(false);
  const [allDiscoveries, setAllDiscoveries] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [listenModal, setListenModal] = useState({
    isOpen: false, artistName: null, albumName: null, ids: {}, mode: 'listen'
  });
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 769 : false
  );
  const [showWallView, setShowWallView] = useState(
    () => (typeof window !== 'undefined' && window.innerWidth >= 769 && !!state?.showWall)
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 769);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Carica discovery di oggi e lista completa
  useEffect(() => {
    const loadInitialDiscovery = async () => {
      if (state?.showWall && typeof window !== 'undefined' && window.innerWidth >= 769) {
        setShowWallView(true);
        setDiscovery(null);
        await loadAllDiscoveries();
        return;
      }

      if (state?.showDetail && state?.discoveryId) {
        setShowWallView(false);
        setIntroExpanded(false);
        await loadDiscoveryById(state.discoveryId);
        await loadAllDiscoveries();
        return;
      }

      setShowWallView(false);

      // Se arriva dal box Home con loadLatest, IGNORA la memoria e carica l'ultimo
      if (state?.loadLatest) {
        console.log('[DailyDiscovery] loadLatest flag detected - loading today discovery');
        // Carica l'ultimo (oggi)
        await loadTodayDiscovery();
        // NON aggiornare la memoria qui - verrà aggiornata dall'altro useEffect
        return;
      }
      
      // Altrimenti usa la memoria: controlla se c'è una discovery salvata nella sessione
      const savedDiscoveryId = sessionStorage.getItem('current_discovery_id');
      console.log('[DailyDiscovery] Checking memory:', savedDiscoveryId);
      
      if (savedDiscoveryId) {
        // Carica la discovery salvata (memoria)
        console.log('[DailyDiscovery] Loading from memory:', savedDiscoveryId);
        await loadDiscoveryById(parseInt(savedDiscoveryId, 10));
      } else {
        // Altrimenti carica quella di oggi
        console.log('[DailyDiscovery] No memory - loading today');
        await loadTodayDiscovery();
      }
    };
    
    loadInitialDiscovery();
    loadAllDiscoveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, state?.loadLatest, state?.timestamp, state?.showWall, state?.showDetail, state?.discoveryId]);

  // Salva la discovery corrente in sessionStorage quando cambia
  useEffect(() => {
    if (discovery?.id) {
      sessionStorage.setItem('current_discovery_id', discovery.id.toString());
    }
  }, [discovery?.id]);

  const resolveRepresentativeArtist = async (artists) => {
    if (!artists?.length) return null;

    // 1) prima immagine già in DB (1°, 2°, 3°…) — salta placeholder Deezer
    for (const artist of artists) {
      const artistName = typeof artist === 'string' ? artist : artist.name;
      const artistImageUrl = typeof artist === 'object' ? (artist.image_url || null) : null;
      if (isUsableArtistImageUrl(artistImageUrl)) {
        return { name: artistName, images: [{ url: artistImageUrl }] };
      }
    }

    // 2) lookup provider in ordine finché non troviamo una cover vera
    for (const artist of artists) {
      const artistName = typeof artist === 'string' ? artist : artist.name;
      const artistGenre = typeof artist === 'object' ? artist.genre : null;
      const artistOrigin = typeof artist === 'object' ? artist.origin : null;
      const artistSpotifyId = typeof artist === 'object'
        ? (artist.spotify_id || artist.spotifyId || null)
        : null;
      try {
        const providerData = await searchArtistForProvider(
          artistName, artistSpotifyId, artistGenre, artistOrigin, true
        );
        const url = providerData?.images?.find((img) => isUsableArtistImageUrl(img?.url))?.url;
        if (url) {
          return { name: artistName, images: providerData.images.filter((img) => isUsableArtistImageUrl(img?.url)) };
        }
      } catch (e) {
        // prova il prossimo
      }
    }
    return null;
  };

  const loadTodayDiscovery = async () => {
    try {
      const data = await DailyDiscoveryService.getTodayDiscovery(language);
      
      if (!data) {
        console.log('[Daily Discovery] No discovery available');
        return;
      }

      setDiscovery(data);
      setRepresentativeArtist(null);
      setLoadingArtistImage(true);

      if (data.artists && data.artists.length > 0) {
        try {
          const rep = await resolveRepresentativeArtist(data.artists);
          if (rep) setRepresentativeArtist(rep);
        } catch (error) {
          console.log('Could not load representative artist');
        } finally {
          setLoadingArtistImage(false);
        }
      } else {
        setLoadingArtistImage(false);
      }

    } catch (error) {
      console.log('[Daily Discovery] Load error:', error.message);
      setLoadingArtistImage(false);
    }
  };

  const loadAllDiscoveries = async () => {
    try {
      setLoadingList(true);
      const data = await DailyDiscoveryService.getAllDiscoveries(language);
      setAllDiscoveries(data);
    } catch (error) {
      console.error('[Daily Discovery] Error loading all discoveries:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const loadDiscoveryById = async (discoveryId) => {
    try {
      const data = await DailyDiscoveryService.getDiscoveryById(discoveryId, language);
      
      if (!data) {
        console.warn('[DailyDiscovery] Discovery not found, loading latest...');
        // Se non esiste più (es. cancellato), carica l'ultimo disponibile
        await loadTodayDiscovery();
        return;
      }

      setDiscovery(data);
      setShowDropdown(false);
      setShowWallView(false);

      setRepresentativeArtist(null);
      setLoadingArtistImage(true);

      if (data.artists && data.artists.length > 0) {
        try {
          const rep = await resolveRepresentativeArtist(data.artists);
          if (rep) setRepresentativeArtist(rep);
        } catch (error) {
          console.log('Could not load representative artist');
        } finally {
          setLoadingArtistImage(false);
        }
      } else {
        setLoadingArtistImage(false);
      }

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('[Daily Discovery] Error loading discovery:', error);
      // In caso di errore, carica l'ultimo disponibile
      await loadTodayDiscovery();
    }
  };

  const handleDiscoverArtists = () => {
    if (!discovery || !discovery.artists) return;

    console.log('[DailyDiscovery] Navigating to artists, discovery ID:', discovery.id);

    // Naviga a ShowResult passando gli artisti come items con type 'daily-discovery-artists'
    // Gestisci sia array di stringhe (vecchio formato) che array di oggetti (nuovo formato)
    const mappedArtists = discovery.artists.map((artist, index) => {
      const artistName = typeof artist === 'string' ? artist : artist.name;
      const artistGenre = typeof artist === 'object' ? artist.genre : null;
      const artistOrigin = typeof artist === 'object' ? artist.origin : null;
      const artistSpotifyId = typeof artist === 'object'
        ? (artist.spotify_id || artist.spotifyId || null)
        : null;
      
      console.log(`[DailyDiscovery] Mapping artist ${index + 1}: "${artistName}" (genre: ${artistGenre}, origin: ${artistOrigin})`);
      
      return {
        name: artistName,
        genre: artistGenre,
        origin: artistOrigin,
        rank: index + 1,
        url: `https://www.last.fm/music/${encodeURIComponent(artistName)}`,
        spotifyId: artistSpotifyId,
        spotify_id: artistSpotifyId
      };
    });
    
    console.log(`[DailyDiscovery] Passing ${mappedArtists.length} artists to ShowResult:`, mappedArtists.map(a => a.name));
    
    navigate('/daily-discovery-artists', {
      state: {
        type: 'daily-discovery-artists',
        items: mappedArtists,
        query: discovery.title,
        fromDiscovery: true,
        discoveryId: discovery.id
      }
    });
  };

  if (showWallView && isDesktop) {
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return language === 'it'
        ? date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
        : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    return (
      <div className="discoveries-wall">
        <PageHeader
          title={t('dailyDiscovery.wallTitle')}
          subtitle={t('dailyDiscovery.wallSubtitle')}
        />

        {loadingList ? (
          <p className="discoveries-wall__status">{t('dailyDiscovery.loadingList')}</p>
        ) : allDiscoveries.length === 0 ? (
          <p className="discoveries-wall__status">{t('dailyDiscovery.noOtherDiscoveries')}</p>
        ) : (
          <div className="discoveries-wall__grid">
            {allDiscoveries.map((disc) => (
              <button
                key={disc.id}
                type="button"
                className="discoveries-wall__card"
                onClick={() => {
                  navigate('/daily-discovery', {
                    state: {
                      type: 'daily-discovery',
                      showDetail: true,
                      discoveryId: disc.id,
                      timestamp: Date.now()
                    }
                  });
                }}
              >
                <div className="discoveries-wall__card-photo" aria-hidden="true">
                  <DiscoveryWallCover
                    coverImage={disc.cover_image}
                    coverUrls={disc.cover_urls}
                    coverCandidates={disc.cover_candidates}
                  />
                </div>
                <div className="discoveries-wall__card-scrim" aria-hidden="true" />
                <div className="discoveries-wall__card-body">
                  <span className="discoveries-wall__card-date">{formatDate(disc.discovery_date)}</span>
                  <h3>{disc.title}</h3>
                  {disc.tags?.length > 0 && (
                    <div className="discoveries-wall__tags">
                      {disc.tags.slice(0, 3).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                  <span className="discoveries-wall__card-meta">
                    {disc.artist_count || 0} {language === 'it' ? 'artisti' : 'artists'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!discovery) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <p>{t('dailyDiscovery.loading')}</p>
      </div>
    );
  }

  const artistImageUrl = representativeArtist?.images?.[0]?.url;

  // Formatta la data per visualizzazione
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return language === 'it' 
      ? date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
      : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const introParagraphs = (() => {
    if (!discovery.introduction) return [];
    const paragraphs = discovery.introduction.split('\n\n');
    return paragraphs.filter((para, index) => {
      if (index === 0) {
        const paraTrimmed = para.trim();
        const titleTrimmed = discovery.title.trim();
        if (paraTrimmed === titleTrimmed ||
            /^\d+\/\d+\s+/.test(paraTrimmed) ||
            paraTrimmed.startsWith(titleTrimmed + ':') ||
            paraTrimmed.startsWith(titleTrimmed + '.')) {
          return false;
        }
      }
      return true;
    });
  })();

  const mappedArtists = (discovery.artists || []).map((artist, index) => {
    const artistName = typeof artist === 'string' ? artist : artist.name;
    return {
      name: artistName,
      genre: typeof artist === 'object' ? artist.genre : null,
      origin: typeof artist === 'object' ? artist.origin : null,
      rank: index + 1,
      image_url: typeof artist === 'object' ? artist.image_url : null,
      spotifyId: typeof artist === 'object' ? (artist.spotify_id || artist.spotifyId || null) : null,
      spotify_id: typeof artist === 'object' ? (artist.spotify_id || artist.spotifyId || null) : null,
      deezer_id: typeof artist === 'object' ? artist.deezer_id : null,
    };
  });

  // Desktop: titolo + intro clamp + griglia artisti (niente immagine tonda / testo laterale)
  if (isDesktop) {
    return (
      <div className="discovery-detail">
        <button
          type="button"
          className="discoveries-wall__back"
          onClick={() => {
            navigate('/daily-discovery', {
              state: { type: 'daily-discovery', showWall: true, timestamp: Date.now() }
            });
          }}
        >
          ← {t('dailyDiscovery.backToWall')}
        </button>

        <PageHeader
          title={discovery.title}
          subtitle={formatDate(discovery.discovery_date)}
        />

        {discovery.tags?.length > 0 && (
          <div className="discovery-detail__tags">
            {discovery.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}

        <div className="discovery-detail__intro">
          <div className={`discovery-detail__intro-text ${introExpanded ? '' : 'is-clamped'}`}>
            {introParagraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          {introParagraphs.length > 0 && (
            <button
              type="button"
              className="discovery-detail__more"
              onClick={() => setIntroExpanded((v) => !v)}
            >
              {introExpanded ? t('dailyDiscovery.readLess') : t('dailyDiscovery.readMore')}
            </button>
          )}
        </div>

        <h3 className="page-header__title" style={{ fontSize: '1.25rem', marginBottom: 16 }}>
          {t('dailyDiscovery.artistsTitle')}
        </h3>
        <div className="discovery-detail__artists">
          {mappedArtists.map((artist, index) => (
            <ArtistCard
              key={`${artist.name}-${index}`}
              variant="grid"
              artist={artist}
              onOpenListenModal={(artistName, albumName, ids, mode) =>
                setListenModal({
                  isOpen: true,
                  artistName,
                  albumName,
                  ids: ids || {},
                  mode: mode || 'listen'
                })
              }
            />
          ))}
        </div>

        <ListenModal
          isOpen={listenModal.isOpen}
          onClose={() => setListenModal({ isOpen: false, artistName: null, albumName: null, ids: {}, mode: 'listen' })}
          artistName={listenModal.artistName}
          albumName={listenModal.albumName}
          ids={listenModal.ids}
          mode={listenModal.mode}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      paddingBottom: '40px',
      overflow: 'auto',
      position: 'relative'
    }}>
      <div style={{
        padding: '40px 20px 40px'
      }}>
        {/* Dropdown Selector per altri approfondimenti */}
        <div style={{
          maxWidth: '700px',
          margin: '0 auto 30px',
          position: 'relative'
        }}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              width: '100%',
              padding: '12px 20px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            <span>
              <i className="bi bi-list-ul" style={{ marginRight: '10px' }} />
              {t('dailyDiscovery.allDiscoveries')}
            </span>
            <i className={`bi bi-chevron-${showDropdown ? 'up' : 'down'}`} />
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '5px',
              backgroundColor: 'rgba(30,30,30,0.98)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
            }}>
              {loadingList ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  {t('dailyDiscovery.loadingList')}
                </div>
              ) : allDiscoveries.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  {t('dailyDiscovery.noOtherDiscoveries')}
                </div>
              ) : (
                allDiscoveries.map((disc, index) => (
                  <button
                    key={disc.id}
                    onClick={() => loadDiscoveryById(disc.id)}
                    style={{
                      width: '100%',
                      padding: '15px 20px',
                      backgroundColor: disc.id === discovery.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      border: 'none',
                      borderBottom: index < allDiscoveries.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                      color: 'white',
                      textAlign: 'left',
                      cursor: disc.id === discovery.id ? 'default' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      if (disc.id !== discovery.id) {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (disc.id !== discovery.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span style={{
                      minWidth: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'rgba(255,255,255,0.6)'
                    }}>
                      {allDiscoveries.length - index}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        marginBottom: '4px',
                        color: disc.id === discovery.id ? 'white' : 'rgba(255,255,255,0.9)'
                      }}>
                        {disc.title}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: 'rgba(255,255,255,0.5)' 
                      }}>
                        {formatDate(disc.discovery_date)} • {disc.artist_count || 0} {language === 'it' ? 'artisti' : 'artists'}
                      </div>
                    </div>
                    {disc.id === discovery.id && (
                      <i className="bi bi-check-circle-fill" style={{ 
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '16px' 
                      }} />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Immagine artista rappresentativo */}
        <div style={{
          width: '200px',
          height: '200px',
          margin: '0 auto 30px',
          position: 'relative'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            backgroundColor: loadingArtistImage ? '#2a2a2a' : (artistImageUrl ? 'transparent' : '#555'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {artistImageUrl && !loadingArtistImage && (
              <img
                src={artistImageUrl}
                alt={representativeArtist.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            )}
          </div>
          {/* Spinning border quando sta caricando */}
          {loadingArtistImage && (
            <div className="spinning-border" />
          )}
        </div>

        {/* Titolo */}
        <h1 style={{
          fontSize: 'clamp(24px, 5vw, 38px)',
          fontWeight: '700',
          marginBottom: '10px',
          textAlign: 'center',
          color: 'white',
          lineHeight: '1.2',
          padding: '0 10px'
        }}>
          {discovery.title}
        </h1>

        {/* Tags */}
        {discovery.tags && discovery.tags.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            marginTop: '20px',
            marginBottom: '30px',
            flexWrap: 'wrap',
            padding: '0 10px'
          }}>
            {discovery.tags.map((tag, i) => (
              <span key={i} style={{
                padding: '5px 15px',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '20px',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)'
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Introduzione */}
        <div style={{
          fontSize: '15px',
          lineHeight: '1.75',
          color: 'rgba(255,255,255,0.85)',
          textAlign: 'left',
          marginBottom: '40px',
          padding: '0 15px',
          maxWidth: '700px',
          margin: '0 auto 40px'
        }}>
          {introParagraphs.map((paragraph, index) => (
            <p key={index} style={{ marginBottom: '20px', marginTop: 0 }}>
              {paragraph}
            </p>
          ))}
        </div>

        {/* Pulsante Discover Artists */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px',
          paddingBottom: '40px'
        }}>
          <button
            onClick={handleDiscoverArtists}
            style={{
              padding: '15px 40px',
              backgroundColor: 'white',
              color: '#121212',
              border: 'none',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 6px 25px rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(255,255,255,0.2)';
            }}
          >
            {t('dailyDiscovery.discoverArtists')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyDiscovery;
